import { useCallback, useEffect, useMemo, useState } from 'react';
import './Dashboard.scss';
import CalendarIcon from '../../assets/icons/calendar.svg';
import DashboardAmountInvestedIcon from '../../assets/icons/dashboard-amount-invested.svg';
import DashboardProfitsEarnedIcon from '../../assets/icons/dashboard-profits-earned.svg';
import DashboardAmountDueIcon from '../../assets/icons/dashboard-amount-due.svg';
import DashboardAvgReturnsIcon from '../../assets/icons/dashboard-avg-returns.svg';
import DashboardDealsInvestedIcon from '../../assets/icons/dashboard-deals-invested.svg';
import RecommendedDeals from './Components/RecommendedDeals';
import IndustriesChart from './Components/IndustriesChart';
import TrendBadge from '../../components/Dashboard/TrendBadge';
import { useSelector, useDispatch } from 'react-redux';
import { Image, Typography, Select, Tooltip } from 'antd';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { getDashboardDetails } from '../../redux/slices/dashboardSlice';
import axios from '../../config/httpConfig';
import { formatCurrency, calculateDateRange, calculateDateRangeForComparison } from '../../helpers/helpers';
import { dashFilterOptions } from '../../constants/constants';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const LAKH = 100000;

const calculateMetricTrend = (currentValue, previousValue) => {
	const current = Number(currentValue || 0);
	const previous = Number(previousValue || 0);

	if (current === 0 && previous === 0) {
		return {
			trend: '0.00%',
			trendType: 'neutral',
		};
	}

	if (previous === 0) {
		return {
			trend: '100.00%',
			trendType: 'up',
		};
	}

	const changePercent = ((current - previous) / Math.abs(previous)) * 100;

	return {
		trend: `${Math.abs(changePercent).toFixed(2)}%`,
		trendType: changePercent >= 0 ? 'up' : 'down',
	};
};

const Dashboard = () => {
	const dispatch = useDispatch();
	const [selectedFilter, setSelectedFilter] = useState('ORV0004');
	const [startDate, setStartDate] = useState(() => calculateDateRange('ORV0004').start);
	const [endDate, setEndDate] = useState(() => calculateDateRange('ORV0004').end);
	const [comparisonData, setComparisonData] = useState({});

	const { user } = useSelector((state) => state.auth);
	const { dashboardData } = useSelector((state) => state.dashboard);
	const selectedFilterOption = dashFilterOptions.find((option) => option.filterId === selectedFilter);
	const comparisonText = selectedFilterOption?.comparisonText || 'vs Last Month';

	useEffect(() => {
		const { start, end } = calculateDateRange(selectedFilter);
		setStartDate(start);
		setEndDate(end);
	}, [selectedFilter]);

	useEffect(() => {
		if (user?.orgId) {
			dispatch(getDashboardDetails({ orgId: user.orgId, startDate, endDate }));
		}
	}, [dispatch, user?.orgId, startDate, endDate]);

	useEffect(() => {
		const fetchComparisonMetrics = async () => {
			if (!user?.orgId) {
				setComparisonData({});
				return;
			}

			const comparisonRange = calculateDateRangeForComparison(selectedFilter, startDate, endDate);

			if (!comparisonRange) {
				setComparisonData({});
				return;
			}

			try {
				const { start, end } = comparisonRange;
				const response = await axios.get(
					`/api/investor/getDashboardDetails?orgId=${user.orgId}&startDate=${start}&endDate=${end}`,
				);
				setComparisonData(response?.response || {});
			} catch (error) {
				console.error('Error fetching dashboard comparison metrics:', error);
				setComparisonData({});
			}
		};

		fetchComparisonMetrics();
	}, [user?.orgId, selectedFilter, startDate, endDate]);

	const portfolioTrend = useMemo(() => {
		return (dashboardData?.graph || []).map((item) => {
			if (item.label) {
				return {
					label: item.label,
					amountInvested: Number(item.amount || 0),
				};
			}

			const parts = String(item.date || "").split("-").map(Number);
			const [year, month, day] = parts;
			const labelDate =
				Number.isFinite(year) && Number.isFinite(month)
					? new Date(year, month - 1, day || 1)
					: new Date(item.date);

			const label = Number.isFinite(day)
				? labelDate.toLocaleString("en-US", { month: "short", day: "numeric" })
				: labelDate.toLocaleString("en-US", { month: "short", year: "numeric" });

			return {
				label,
				amountInvested: Number(item.amount || 0),
			};
		});
	}, [dashboardData]);

	const yAxisTicks = useMemo(() => {
		const maxValue = Math.max(...portfolioTrend.map((item) => Number(item.amountInvested || 0)), 0);
		const maxLakh = Math.ceil(maxValue / LAKH) || 1;
		const step = maxLakh <= 10 ? 2 : 10;
		const upperLakh = Math.max(step, Math.ceil(maxLakh / step) * step);
		const ticks = [];

		for (let lakh = 0; lakh <= upperLakh; lakh += step) {
			ticks.push(lakh * LAKH);
		}

		return ticks;
	}, [portfolioTrend]);

	const totalInvested = Number(dashboardData?.totalAmountInvested || 0);
	const totalReturnDue = Number(dashboardData?.totalReturnDue || 0);
	const totalAvgReturns = Number(dashboardData?.totalAvgReturns || 0);
	const totalDealsInvested = Number(dashboardData?.totalDealsInvested || 0);
	const totalEarned = Number(dashboardData?.totalProfitsEarned || 0);
	const totalPortfolioValue = totalInvested + totalEarned;

	const investedTrend = useMemo(
		() => calculateMetricTrend(totalInvested, comparisonData?.totalAmountInvested),
		[totalInvested, comparisonData?.totalAmountInvested]
	);
	const dueTrend = useMemo(
		() => calculateMetricTrend(totalReturnDue, comparisonData?.totalReturnDue),
		[totalReturnDue, comparisonData?.totalReturnDue]
	);
	const avgReturnsTrend = useMemo(
		() => calculateMetricTrend(totalAvgReturns, comparisonData?.totalAvgReturns),
		[totalAvgReturns, comparisonData?.totalAvgReturns]
	);
	const dealsTrend = useMemo(
		() => calculateMetricTrend(totalDealsInvested, comparisonData?.totalDealsInvested),
		[totalDealsInvested, comparisonData?.totalDealsInvested]
	);
	const profitsTrend = useMemo(
		() => calculateMetricTrend(totalEarned, comparisonData?.totalProfitsEarned),
		[totalEarned, comparisonData?.totalProfitsEarned]
	);
	const comparisonPortfolioValue = Number(comparisonData?.totalAmountInvested || 0)
		+ Number(comparisonData?.totalProfitsEarned || 0);
	const portfolioValueTrend = useMemo(
		() => calculateMetricTrend(totalPortfolioValue, comparisonPortfolioValue),
		[totalPortfolioValue, comparisonPortfolioValue]
	);

	const metricCards = useMemo(() => [
		{
			title: 'Amount Invested',
			tooltip: 'Total capital you have invested across all live and closed deals.',
			icon: DashboardAmountInvestedIcon,
			value: formatCurrency(totalInvested),
			trend: investedTrend.trend,
			trendType: investedTrend.trendType,
		},
		{
			title: 'Profits Earned',
			tooltip: 'Total returns earned from the completed investments',
			icon: DashboardProfitsEarnedIcon,
			value: formatCurrency(totalEarned),
			trend: profitsTrend.trend,
			trendType: profitsTrend.trendType,
		},
		{
			title: 'Amount Due',
			tooltip: 'Expected amount pending to be received from active investments',
			icon: DashboardAmountDueIcon,
			value: formatCurrency(totalReturnDue),
			trend: dueTrend.trend,
			trendType: dueTrend.trendType,
		},
		{
			title: 'Avg Returns',
			tooltip: 'Average return earned across all your investments.',
			icon: DashboardAvgReturnsIcon,
			value: `${(totalAvgReturns * 100).toFixed(2)}%`,
			trend: avgReturnsTrend.trend,
			trendType: avgReturnsTrend.trendType,
		},
		{
			title: 'Deals Invested',
			tooltip: 'Total number of deals you have participated in',
			icon: DashboardDealsInvestedIcon,
			value: totalDealsInvested,
			trend: dealsTrend.trend,
			trendType: dealsTrend.trendType,
		},

	], [
		totalInvested,
		totalEarned,
		totalReturnDue,
		totalAvgReturns,
		totalDealsInvested,
		investedTrend,
		profitsTrend,
		dueTrend,
		avgReturnsTrend,
		dealsTrend,
	]);

	const renderPortfolioTooltip = useCallback(({ active, payload, label }) => {
		if (!active || !payload || payload.length === 0) {
			return null;
		}

		const amount = Number(payload[0]?.payload?.amountInvested || payload[0]?.value || 0);

		return (
			<div
				style={{
					background: '#ffffff',
					borderRadius: 10,
					padding: '10px 12px',
					boxShadow: '0 8px 20px rgba(8, 19, 40, 0.18)',
				}}
			>
				<div style={{ color: '#0b1f42', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{label}</div>
				<div style={{ color: '#17a565', fontSize: 14, fontWeight: 700 }}>
					{`Amount Invested - ${formatCurrency(amount)}`}
				</div>
			</div>
		);
	}, []);

	return (
		<div className='dashboard-container'>
			<div className='dashboard-title-row'>
				<div>
					<Title level={2}>Your Dashboard</Title>
					<Text className='dashboard-subtitle'>Here&apos;s what&apos;s happening with your investments</Text>
				</div>

				<Select
					value={selectedFilter}
					onChange={setSelectedFilter}
					className='dashboard-filter-select'
					options={dashFilterOptions.map((option) => ({
						value: option.filterId,
						label: option.filterName,
					}))}
					suffixIcon={<Image src={CalendarIcon} alt='Calendar' preview={false} />}
				/>
			</div>

			<>
				<div className='portfolio-card'>
					<div className='portfolio-card-left'>
						<Text className='portfolio-label'>
							Total Portfolio Value
							<Tooltip title="Current value of all your investments, including earned returns."> <InfoCircleOutlined /> </Tooltip>
						</Text>
						<Title level={2}>{formatCurrency(totalPortfolioValue)}</Title>
						<Text className='portfolio-breakdown'>
							Total: {formatCurrency(totalInvested)} Invested
							<span>•</span>
							{formatCurrency(totalEarned)} Returns
						</Text>

						<TrendBadge
							trend={portfolioValueTrend.trend}
							trendType={portfolioValueTrend.trendType}
							comparisonText={comparisonText}
						/>
					</div>

					<div className='portfolio-card-right'>
						<div className='portfolio-card-tooltip'>
							<Tooltip title="Investment amounts over the selected filter period.">
								<InfoCircleOutlined />
							</Tooltip>
						</div>
						<ResponsiveContainer width='100%' height={140}>
							{portfolioTrend.length > 0 ? (
								<AreaChart data={portfolioTrend} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
									<defs>
										<linearGradient id='dashboardLineFill' x1='0' y1='0' x2='0' y2='1'>
											<stop offset='5%' stopColor='#44d27a' stopOpacity={0.55} />
											<stop offset='95%' stopColor='#44d27a' stopOpacity={0} />
										</linearGradient>
									</defs>
									<XAxis dataKey='label' axisLine={false} tickLine={false} tick={{ fill: '#d3ddf2', fontSize: 11 }} />
									<YAxis
										axisLine={false}
										tickLine={false}
										ticks={yAxisTicks}
										domain={[0, yAxisTicks[yAxisTicks.length - 1] || LAKH * 30]}
										tick={{ fill: '#d3ddf2', fontSize: 11 }}
										tickFormatter={(value) => `${Math.round(Number(value || 0) / LAKH)}L`}
									/>
									<RechartsTooltip content={renderPortfolioTooltip} cursor={{ stroke: '#dbe7ff', strokeWidth: 2 }} />
									<Area
										type='monotone'
										dataKey='amountInvested'
										stroke='#44d27a'
										strokeWidth={2}
										fill='url(#dashboardLineFill)'
										dot={{ r: 2, stroke: '#7df2a7', strokeWidth: 1, fill: '#1b4f99' }}
										activeDot={{ r: 3, stroke: '#7df2a7', strokeWidth: 1, fill: '#1b4f99' }}
									/>
								</AreaChart>
							) : (
								<div className="no-data-container">
									No portfolio data available
								</div>
							)}
						</ResponsiveContainer>

					</div>
				</div>

				<div className='metrics-row'>
					{metricCards.map((card) => (
						<div key={card.title} className='metric-card'>
							<div className='metric-icon-wrap'>
								<Image src={card.icon} alt={card.title} preview={false} />
							</div>

							<Text className='metric-title'>{card.title} <Tooltip title={card.tooltip}>
								<InfoCircleOutlined className='info-icon' />
							</Tooltip>
							</Text>
							<Title level={3}>{card.value}</Title>

							<TrendBadge
								className='metric-trend'
								trend={card.trend}
								trendType={card.trendType}
								comparisonText={comparisonText}
							/>
						</div>
					))}
				</div>

				<RecommendedDeals dashboardData={dashboardData} />
				<IndustriesChart filterOptions={dashFilterOptions} dashboardData={dashboardData} />
			</>
		</div>
	);
};

export default Dashboard;
